import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "animate.css";

import Navbar from "../Navbar/Navbar";
import MainBodyContainer from "../Common/Body/MainBodyContainer/MainBodyContainer";
import Footer from "../Footer/Footer";
import { CommunityProjectsData } from "../Data/CommunityProjectsData";

import "./CommunityProjects.css";

// Animated Counter Component
const AnimatedCounter = ({ value, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const counterRef = useRef(null);

  // Extract prefix (like £) and suffix (like +)
  const prefix = value.match(/^[^0-9]*/)?.[0] || '';
  const suffix = value.match(/[0-9]+(.*)$/)?.[1] || '';
  const numericValue = parseInt(value.replace(/[^0-9]/g, ''));

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible || hasAnimated) return;
    
    setHasAnimated(true);
    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentCount = Math.floor(numericValue * easeOut);
      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Ensure final value is exact
        setCount(numericValue);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, hasAnimated, numericValue, duration]);
  
  return (
    <span ref={counterRef}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

// Approach Cards Data
const approachCards = [
  {
    icon: "🏠",
    title: "Housing Stability First",
    preview: "Stable homes are the foundation for everything else.",
    detail: "A family facing eviction can't focus on job searching. We work to prevent homelessness and secure stable housing so families can thrive."
  },
  {
    icon: "👨‍👩‍👧‍👦",
    title: "Family-Centered",
    preview: "Supporting one family member supports the whole family.",
    detail: "Parents learning English can help children with homework. We strengthen families so they can support each other."
  },
  {
    icon: "🌟",
    title: "Trauma-Informed Care",
    preview: "Many community members have experienced trauma.",
    detail: "We provide support that acknowledges trauma, builds trust, and creates safe spaces for healing and growth."
  },
  {
    icon: "🤝",
    title: "Community Networks",
    preview: "Isolation is a barrier to thriving.",
    detail: "We create spaces for people to connect, build lasting bonds, and support each other through peer networks."
  },
  {
    icon: "⚖️",
    title: "Rights & Justice",
    preview: "We advocate for systemic change.",
    detail: "We ensure community members understand and can fight for their rights in housing, benefits, and healthcare."
  },
  {
    icon: "🌍",
    title: "Cultural Sensitivity",
    preview: "Respect for diverse backgrounds is essential.",
    detail: "Our multilingual staff and culturally appropriate services ensure everyone feels welcomed and understood."
  }
];

// Timeline Data
const timelinePhases = [
  {
    icon: "⚠️",
    time: "THE CRISIS",
    title: "Facing Eviction",
    description: "An Afghan refugee family of six faced eviction with just two weeks' notice. Children at risk of changing schools, limited English, and complete isolation."
  },
  {
    icon: "🤝",
    time: "APT'S RESPONSE",
    title: "Holistic Support",
    description: "Advocacy secured housing extension, ESOL for parents, children joined coding camp and activities, connected to Afghan community network."
  },
  {
    icon: "✨",
    time: "TRANSFORMATION",
    title: "Thriving Family",
    description: "18 months later: permanent housing, employed parents, daughter pursuing IT career. They now support other newly arrived families."
  }
];

// Project Icons
const projectIcons = {
  'advocacy-services': '⚖️',
  'community-outreach': '🤝'
};

const CommunityProjects = () => {
  const [expandedCard, setExpandedCard] = useState(-1);
  const [activePhase, setActivePhase] = useState(0);
  const [showStickyNav, setShowStickyNav] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  
  const heroRef = useRef(null);
  const impactRef = useRef(null);
  const approachRef = useRef(null);
  const projectsRef = useRef(null);
  const storyRef = useRef(null);
  const accessRef = useRef(null);

  // Sticky Navigation
  useEffect(() => {
    const handleScroll = () => {
      const heroBottom = heroRef.current?.getBoundingClientRect().bottom || 0;
      setShowStickyNav(heroBottom < 0);

      // Update active section based on scroll position
      const sections = [
        { id: 'impact', ref: impactRef },
        { id: 'approach', ref: approachRef },
        { id: 'projects', ref: projectsRef },
        { id: 'story', ref: storyRef },
        { id: 'access', ref: accessRef }
      ];

      for (const section of sections) {
        const rect = section.ref.current?.getBoundingClientRect();
        if (rect && rect.top < 200 && rect.bottom > 200) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const sectionMap = {
      'impact': impactRef,
      'approach': approachRef,
      'projects': projectsRef,
      'story': storyRef,
      'access': accessRef
    };
    
    const ref = sectionMap[sectionId];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Impact metrics
  const impactMetrics = [
    { number: "750+", label: "People Supported", description: "This year alone", icon: "👥" },
    { number: "£45,000+", label: "Benefits Secured", description: "Financial impact", icon: "💷" },
    { number: "170+", label: "Advocacy Cases", description: "Systems navigated", icon: "⚖️" },
    { number: "100+", label: "Families Reached", description: "Holistic support", icon: "🏠" }
  ];

  return (
    <div>
      <Navbar />
      
      {/* Sticky Navigation */}
      {showStickyNav && (
        <nav className="cp-sticky-nav">
          {[
            { id: 'impact', label: 'Our Impact' },
            { id: 'approach', label: 'Our Approach' },
            { id: 'projects', label: 'Projects' },
            { id: 'story', label: 'Real Stories' },
            { id: 'access', label: 'Get Help' }
          ].map(section => (
            <button
              key={section.id}
              className={activeSection === section.id ? 'active' : ''}
              onClick={() => scrollToSection(section.id)}
            >
              {section.label}
            </button>
          ))}
        </nav>
      )}

      <div>
        <MainBodyContainer heading={undefined}>
          {/* Hero Section */}
          <section ref={heroRef} className="cp-hero">
            <div className="cp-hero-bg">
              <div className="hero-grid-pattern"></div>
              <div className="floating-symbol symbol-1">🤝</div>
              <div className="floating-symbol symbol-2">🏠</div>
              <div className="floating-symbol symbol-3">⚖️</div>
              <div className="floating-symbol symbol-4">❤️</div>
              <div className="floating-symbol symbol-5">🌍</div>
              <div className="floating-symbol symbol-6">🌟</div>
              <div className="hero-orb orb-1"></div>
              <div className="hero-orb orb-2"></div>
              <div className="hero-orb orb-3"></div>
            </div>

            <div className="cp-hero-content">
              <span className="cp-hero-badge animate__animated animate__fadeInDown">
                Free & Confidential Support
              </span>
              
              <h1 className="cp-hero-title animate__animated animate__fadeInUp">
                When life feels overwhelming,<br />
                <span className="gradient-text">you don't have to face it alone</span>
              </h1>
              
              <p className="cp-hero-subtitle animate__animated animate__fadeInUp animate__delay-1s">
                Advocacy, family support, and community outreach across East London
              </p>

              <div className="cp-hero-cta-group animate__animated animate__fadeInUp animate__delay-2s">
                <a href="tel:02030266258" className="cp-emergency-btn">
                  <span className="pulse-ring"></span>
                  <span className="btn-icon">📞</span>
                  <span className="btn-text">
                    <span className="btn-label">Need urgent help?</span>
                    <span className="btn-number">020 3026 6258</span>
                  </span>
                </a>
                <Link to="/contact?inquiry=advocacy" className="cp-primary-btn">
                  Get Support Now
                  <span className="btn-arrow">→</span>
                </Link>
              </div>

              <div className="cp-hero-stats animate__animated animate__fadeInUp animate__delay-3s">
                {impactMetrics.map((stat, index) => (
                  <div key={index} className="cp-hero-stat">
                    <span className="stat-number">
                      <AnimatedCounter value={stat.number} duration={2000} />
                    </span>
                    <span className="stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Impact Dashboard */}
          <section ref={impactRef} id="impact" className="cp-impact-section">
            <div className="cp-section-header">
              <h2>Your Community Impact</h2>
              <p>Every number represents a family, a story, a future. These are the real outcomes of your support and our work together.</p>
            </div>
            
            <div className="cp-impact-grid">
              {impactMetrics.map((metric, index) => (
                <div key={index} className="cp-impact-card">
                  <div className="impact-icon">{metric.icon}</div>
                  <div className="impact-number">
                    <AnimatedCounter value={metric.number} />
                  </div>
                  <div className="impact-label">{metric.label}</div>
                  <div className="impact-detail">{metric.description}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Holistic Approach */}
          <section ref={approachRef} id="approach" className="cp-approach-section">
            <div className="cp-section-header">
              <h2>Why Holistic Support Matters</h2>
              <p>We can't separate employment from housing stability, education from mental health, or individual success from family wellbeing. True empowerment requires addressing the whole person's needs.</p>
            </div>

            <div className="cp-approach-grid">
              {approachCards.map((card, index) => (
                <div
                  key={index}
                  className="cp-approach-card"
                  onClick={() => setExpandedCard(expandedCard === index ? -1 : index)}
                >
                  <div className="approach-icon-wrapper">
                    {card.icon}
                  </div>
                  <h3>{card.title}</h3>
                  <p className="approach-preview">{card.preview}</p>
                  
                  {expandedCard === index && (
                    <div className="approach-detail">
                      <p>{card.detail}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Projects Grid */}
          <section ref={projectsRef} id="projects" className="cp-projects-section">
            <div className="cp-section-header">
              <h2>Our Community Projects</h2>
              <p>Each project addresses real barriers our community members face — all services are free, culturally sensitive, and designed with community input.</p>
            </div>

            <div className="cp-projects-grid">
              {CommunityProjectsData.map((project, index) => (
                <div
                  key={index}
                  className={`cp-project-card ${project.id === 'advocacy-services' ? 'urgent' : ''}`}
                >
                  {project.id === 'advocacy-services' && (
                    <div className="urgent-indicator">
                      <span className="urgent-pulse"></span>
                      Immediate Support Available
                    </div>
                  )}
                  
                  <div className="project-image-wrapper">
                    <span className="project-icon">{projectIcons[project.id]}</span>
                  </div>
                  
                  <div className="project-content">
                    <div className="project-header">
                      <h3>{project.title}</h3>
                      <p className="project-tagline">{project.shortDescription}</p>
                    </div>

                    <div className="project-body">
                      <div className="who-we-help">
                        <h4>Who We Support</h4>
                        <ul>
                          {(project.whoThisHelps || project.whoWeReach || []).slice(0, 2).map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="key-services">
                        <h4>Key Services</h4>
                        <div className="service-tags">
                          {(project.servicesProvided || project.coreServices || project.outreachActivities || [])
                            .slice(0, 3)
                            .map((service, i) => (
                              <span key={i} className="service-tag">
                                {service.service || service.activity}
                              </span>
                            ))}
                        </div>
                      </div>
                    </div>

                    <div className="project-footer">
                      <span className="project-stat">
                        {project.totalImpact2024?.casesSupported || 
                         project.impactMetrics?.familiesSupported || 
                         project.impactMetrics?.asylumSeekersSupported || 
                         'Active'}
                      </span>
                      <Link to={`/community-projects/${project.id}`} className="project-link">
                        Learn More <span>→</span>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Impact Story Timeline */}
          <section ref={storyRef} id="story" className="cp-story-section">
            <div className="cp-section-header">
              <h2>From Crisis to Community</h2>
              <p>The Ahmed Family's 18-Month Journey</p>
            </div>

            <div className="cp-timeline">
              <div 
                className="timeline-track"
                style={{ '--progress': `${((activePhase + 1) / timelinePhases.length) * 100}%` }}
              ></div>
              
              {timelinePhases.map((phase, index) => (
                <div
                  key={index}
                  className={`timeline-phase ${activePhase === index ? 'active' : ''}`}
                  onClick={() => setActivePhase(index)}
                >
                  <div className="phase-marker">
                    <span className="phase-icon">{phase.icon}</span>
                  </div>
                  <div className="phase-card">
                    <div className="phase-content">
                      <span className="phase-time">{phase.time}</span>
                      <h3>{phase.title}</h3>
                      <p>{phase.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="cp-story-quote">
              <blockquote>
                "APT didn't just help us individually — they brought our family together and gave us hope. We arrived broken; now we're rebuilding stronger than before."
              </blockquote>
              <cite>— The Ahmed Family</cite>
            </div>
          </section>

          {/* Access & Involve Section */}
          <section ref={accessRef} id="access" className="cp-access-section">
            <div className="cp-section-header">
              <h2>What Brings You Here?</h2>
              <p>Whether you need support or want to help others, we're here for you.</p>
            </div>

            <div className="cp-decision-grid">
              <div className="cp-decision-card need-help">
                <div className="decision-header">
                  <span className="decision-icon">🆘</span>
                  <h3>I Need Help</h3>
                </div>
                
                <div className="decision-options">
                  <div className="option urgent">
                    <span className="option-label">Emergency?</span>
                    <a href="tel:02030266258" className="option-action">Call 020 3026 6258</a>
                  </div>
                  <div className="option">
                    <span className="option-label">Not urgent?</span>
                    <Link to="/contact?inquiry=advocacy" className="option-action">Contact Us →</Link>
                  </div>
                  <div className="option">
                    <span className="option-label">Drop-in</span>
                    <span className="option-detail">Mon-Fri 10am-4pm, No appointment</span>
                  </div>
                </div>
              </div>

              <div className="cp-decision-card want-help">
                <div className="decision-header">
                  <span className="decision-icon">🤝</span>
                  <h3>I Want to Help</h3>
                </div>
                
                <div className="decision-options">
                  <div className="option">
                    <span className="option-label">Volunteer</span>
                    <Link to="/get-involved/volunteer" className="option-action">Learn More →</Link>
                  </div>
                  <div className="option">
                    <span className="option-label">Partner</span>
                    <Link to="/contact?inquiry=partnership" className="option-action">Collaborate →</Link>
                  </div>
                  <div className="option">
                    <span className="option-label">Donate Items</span>
                    <Link to="/contact" className="option-action">What We Need →</Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </MainBodyContainer>
      </div>
      <Footer />
    </div>
  );
};

export default CommunityProjects;
